/**
 * スクロール動画の入口 (docs/features/swipe-motion-clip-plan.md §4.1)。静止画シーンのサムネイルの直下に置く。
 * 新規 / 再編集は選択中の動画素材の generation_metadata (engine) で分ける。モーダルは Remotion Player を含むので遅延読み込み。
 */
import { lazy, Suspense, useState } from 'react';

import { Tooltip } from '../../../common/Tooltip';
import { stillSceneVideoMaterialId } from '../../../../features/scenarios/productionMode';
import type { Scenario } from '../types';
import { useSceneHandlers } from '../hooks/SceneHandlersContext';
import { useSceneStateContext } from '../hooks/SceneStateContext';
import { useMaterialLookup } from './useMaterialLookup';
import styles from '../ScenarioGenerator.module.css';
import '../../../../styles/buttons.css';

const SwipeClipModal = lazy(() => import('../../../../features/swipeClip/SwipeClipModal'));

export interface SwipeClipEntryProps {
  scenario: Scenario;
  scene: Scenario['scenes'][number];
  sceneKey: string;
}

export function SwipeClipEntry({ scenario, scene, sceneKey }: SwipeClipEntryProps) {
  const { handleGenerateSwipeClip } = useSceneHandlers();
  const { sceneVideoLoading, sceneVideoMaterialId } = useSceneStateContext();
  const { findLoadedMaterial } = useMaterialLookup();
  const [open, setOpen] = useState(false);
  if (!handleGenerateSwipeClip || !scene.image_material_id) return null;

  const videoMaterialId = sceneVideoMaterialId[sceneKey] || stillSceneVideoMaterialId(scene);
  const videoMaterial = videoMaterialId ? findLoadedMaterial(videoMaterialId) : null;
  const editing = !!(videoMaterial && (videoMaterial as { generation_metadata?: { engine?: string } }).generation_metadata?.engine === 'swipe_clip');

  return (
    <div className={styles.swipeClipEntry}>
      <Tooltip content={editing ? '保存した設定でスクロール動画を作り直します' : '静止画をスワイプ風にスクロールさせた動画を作り、このシーンに差し込みます (無料)'}>
        <button type="button" className="btn-modal-secondary-sm" disabled={!!sceneVideoLoading[sceneKey]} onClick={() => setOpen(true)}>
          {editing ? 'スクロール動画を編集' : 'スクロール動画'}
        </button>
      </Tooltip>
      {open && (
        <Suspense fallback={null}>
          <SwipeClipModal
            scenario={scenario}
            scene={scene}
            asin={scenario.asin ?? null}
            onClose={() => setOpen(false)}
            onSubmit={(params) => handleGenerateSwipeClip(scenario, scene, params)}
          />
        </Suspense>
      )}
    </div>
  );
}
